import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { FirebaseError } from "firebase/app";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  updatePassword as fbUpdatePassword,
  verifyBeforeUpdateEmail,
  type User,
} from "firebase/auth";
import { getFirebaseAuth } from "./firebase";
import { recordLoginInfo } from "./login-info-writes";
import { recordLegalAcceptance } from "./legal-acceptance";
import { readAdminClaim } from "./admin-claim";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  // True while the ID-token custom claims are being resolved after an auth
  // state change. The admin guard waits on this before deciding to redirect.
  claimsLoading: boolean;
  // True when the signed-in user carries the `admin` custom claim. UX-only —
  // real enforcement is the backend `assertAdmin` gate on every admin callable.
  isAdmin: boolean;
  // True when the signed-in user has an email/password credential. Mirrors
  // Android's `loginProvider == LoginProvider.Email` gate — Google-only users
  // shouldn't see the email/password change UI.
  hasPasswordProvider: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  // Sends a Firebase password-reset link. No actionCodeSettings — the link
  // lands on Firebase's hosted reset page, same as the Android app.
  sendPasswordReset: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  // Sends a verification link to `newEmail`. The auth-side email change only
  // takes effect once the user clicks that link. We reauth unconditionally so
  // a wrong current password fails fast with `auth/wrong-password` rather
  // than later with `auth/requires-recent-login`.
  requestEmailChange: (newEmail: string, currentPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  // Re-establishes a recent login for Google users (needed before enrolling /
  // unenrolling a second factor, which Firebase gates on a fresh sign-in).
  reauthenticateWithGoogle: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        recordLoginInfo(u.uid);
        // The sign-in screens state that continuing means accepting the terms and
        // having read the privacy notice — record that against the versions in force.
        recordLegalAcceptance(u.uid);
        setClaimsLoading(true);
        readAdminClaim(u).then((admin) => {
          // Guard against a stale resolution landing after the user changed.
          if (getFirebaseAuth().currentUser?.uid === u.uid) {
            setIsAdmin(admin);
            setClaimsLoading(false);
          }
        });
      } else {
        setIsAdmin(false);
        setClaimsLoading(false);
      }
    });
    return unsub;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  };

  const signUp = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
  };

  const sendPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(getFirebaseAuth(), email);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Always show the chooser so the user can pick their account if they're
    // signed into multiple. Mirrors the Android picker which always opens
    // GoogleSignIn.getClient(...).signInIntent.
    provider.setCustomParameters({ prompt: "select_account" });
    await signInWithGoogleWithFocusWatchdog(getFirebaseAuth(), provider);
  };

  const signOut = async () => {
    await fbSignOut(getFirebaseAuth());
  };

  const reauthenticateWithPassword = async (currentPassword: string) => {
    const auth = getFirebaseAuth();
    const u = auth.currentUser;
    if (!u || !u.email) throw new Error("missing-user");
    const cred = EmailAuthProvider.credential(u.email, currentPassword);
    await reauthenticateWithCredential(u, cred);
  };

  const reauthenticateWithGoogle = async () => {
    const auth = getFirebaseAuth();
    const u = auth.currentUser;
    if (!u) throw new Error("missing-user");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    await reauthenticateWithPopup(u, provider);
  };

  const requestEmailChange = async (
    newEmail: string,
    currentPassword: string
  ) => {
    await reauthenticateWithPassword(currentPassword);
    const u = getFirebaseAuth().currentUser;
    if (!u) throw new Error("missing-user");
    await verifyBeforeUpdateEmail(u, newEmail);
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    await reauthenticateWithPassword(currentPassword);
    const u = getFirebaseAuth().currentUser;
    if (!u) throw new Error("missing-user");
    await fbUpdatePassword(u, newPassword);
  };

  const hasPasswordProvider =
    !!user?.providerData.some((p) => p.providerId === "password");

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        claimsLoading,
        isAdmin,
        hasPasswordProvider,
        signIn,
        signUp,
        sendPasswordReset,
        signInWithGoogle,
        signOut,
        requestEmailChange,
        changePassword,
        reauthenticateWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Layers a window-focus watchdog over `signInWithPopup`. Firebase's own
// popup-closed detection polls `popup.closed` every ~2s and waits for a
// post-message grace, so on Chromium-based browsers (especially with
// strict COOP) it can leave the UI hanging in "Conectăm…" for 5–10s
// after the user dismisses the popup. Our parent window regains focus
// the instant the popup closes, so we use that as a much faster
// cancellation signal: when focus returns and the popup hasn't settled
// within a short grace, throw `auth/popup-closed-by-user` ourselves so
// the caller's catch block fires immediately.
//
// Exported only so it can be unit-tested.
export async function signInWithGoogleWithFocusWatchdog(
  auth: ReturnType<typeof getFirebaseAuth>,
  provider: GoogleAuthProvider,
  // Grace window after focus returns before we declare the popup
  // dismissed. Successful auth posts the credential before the popup
  // closes, so signInWithPopup typically resolves before this window
  // expires; the grace exists so a spurious focus event during the
  // post-message handshake doesn't false-fire.
  graceMs = 1500
): Promise<void> {
  const popupPromise = signInWithPopup(auth, provider);

  // Track whether the popup promise has settled so the watchdog stays
  // out of the way once Firebase has answered.
  let settled = false;
  popupPromise.then(
    () => {
      settled = true;
    },
    () => {
      settled = true;
    }
  );

  if (typeof window === "undefined") {
    await popupPromise;
    return;
  }

  await new Promise<void>((resolve, reject) => {
    let graceTimer: number | null = null;
    let armTimer: number | null = null;

    const cleanup = () => {
      window.removeEventListener("focus", onFocus);
      if (armTimer !== null) window.clearTimeout(armTimer);
      if (graceTimer !== null) window.clearTimeout(graceTimer);
    };

    const onFocus = () => {
      if (settled || graceTimer !== null) return;
      graceTimer = window.setTimeout(() => {
        if (settled) return;
        cleanup();
        reject(
          new FirebaseError(
            "auth/popup-closed-by-user",
            "Popup closed by user."
          )
        );
      }, graceMs);
    };

    // Don't arm the focus listener immediately — opening the popup can
    // briefly trigger focus shifts on the parent before the popup
    // actually takes focus, which would trip the watchdog before the
    // user has done anything.
    armTimer = window.setTimeout(() => {
      window.addEventListener("focus", onFocus);
    }, 500);

    popupPromise.then(
      () => {
        cleanup();
        resolve();
      },
      (err) => {
        cleanup();
        reject(err);
      }
    );
  });
}
